<p-toast [style]="{marginTop: '80px'}"></p-toast>
<p-confirmDialog header="Confirmation" icon="pi pi-exclamation-triangle"></p-confirmDialog>

<div class="ym-admin-wrapper">

<!-- ═══════════════════════════════════════════════════════════
     STATISTICS PANEL
════════════════════════════════════════════════════════════════ -->
<div class="ym-stats-panel">
  <div class="ym-stats-header">
    <i class="pi pi-chart-bar"></i>
    <span>Process Statistics</span>
    <span class="ym-stats-subtitle">Active non-composite properties — successful &amp; failed syncs in the last 24 hours</span>
    <span class="ym-stats-refresh-info" *ngIf="statsLastRefresh">
      Refreshed {{ statsLastRefresh | date:'HH:mm:ss' }}
    </span>
    <button pButton pRipple icon="pi pi-refresh" label="Refresh"
      class="p-button p-button-sm p-button-outlined ym-btn-refresh-stats"
      [loading]="statsLoading"
      (click)="loadStats()">
    </button>
  </div>

  <div class="ym-stats-body">
    <ng-container *ngIf="!statsLoading || statsRows.length > 0; else statsSpinner">
      <table class="ym-stats-table">
        <thead>
          <tr>
            <th class="col-process">Process</th>
            <th class="col-num col-success"
                pTooltip="How many times this kind of sync FINISHED successfully in the last 24 hours, across all active properties. Example: 'Rates = 250' means 250 successful rate-sync runs happened since yesterday."
                tooltipPosition="top" [tooltipOptions]="{showDelay: 300}">Synced (24h)</th>
            <th class="col-num col-error"
                pTooltip="How many times this kind of sync FAILED in the last 24 hours. Anything above 0 here is worth investigating in the per-property table below."
                tooltipPosition="top" [tooltipOptions]="{showDelay: 300}">Errors (24h)</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of statsRows">
            <td class="col-process">
              <i class="pi {{ row.icon }}"></i>
              <span>{{ row.label }}</span>
            </td>
            <td class="col-num col-success" [class.has-value]="row.synced > 0">{{ row.synced | number }}</td>
            <td class="col-num col-error"   [class.has-errors]="row.errors > 0">{{ row.errors | number }}</td>
          </tr>
        </tbody>
      </table>
    </ng-container>
    <ng-template #statsSpinner>
      <div class="ym-stats-loading">
        <i class="pi pi-spin pi-spinner"></i>
        <span>Loading statistics…</span>
      </div>
    </ng-template>
  </div>
</div>

<!-- ═══════════════════════════════════════════════════════════
     HEALTH CHECK PANEL
════════════════════════════════════════════════════════════════ -->
<div class="ym-health-panel">

  <div class="ym-health-header">
    <i class="pi pi-heart"></i>
    <span>Property Health</span>
    <span class="ym-stats-subtitle">
      {{ selectedProperties?.length === 1 ? selectedProperties[0].label : 'All active non-composite properties' }}
    </span>
    <span class="ym-stats-refresh-info" *ngIf="healthLastRefresh">
      Refreshed {{ healthLastRefresh | date:'HH:mm:ss' }}
    </span>
    <button pButton pRipple icon="pi pi-refresh" label="Refresh"
      class="p-button p-button-sm p-button-outlined ym-btn-refresh-stats"
      [loading]="healthLoading"
      (click)="loadHealth()">
    </button>
  </div>

  <ng-container *ngIf="!healthLoading || healthSummary; else healthSpinner">
  <div class="ym-health-grid" *ngIf="healthSummary">

    <!-- ── Property Health ──────────────────────────────── -->
    <div class="ym-health-section">
      <div class="ym-health-section-title"><i class="pi pi-building"></i> Property</div>
      <div class="ym-hc-row" *ngFor="let item of healthSummary.propertyHealth"
           [class]="healthSeverityClass(item.severity)"
           [pTooltip]="item.description" tooltipPosition="right" [tooltipOptions]="{showDelay: 300}">
        <i [class]="healthSeverityIcon(item.severity) + ' ym-hc-icon'"></i>
        <span class="ym-hc-label">{{ item.label }}</span>
        <span class="ym-hc-count"
              [class.ym-hc-count-clickable]="item.count > 0"
              (click)="openDrillDown(item)">
          {{ item.count }}
        </span>
      </div>
    </div>

    <!-- ── Reservation Health ───────────────────────────── -->
    <div class="ym-health-section">
      <div class="ym-health-section-title"><i class="pi pi-calendar"></i> Reservations</div>
      <div class="ym-hc-row" *ngFor="let item of healthSummary.reservationHealth"
           [class]="healthSeverityClass(item.severity)"
           [pTooltip]="item.description" tooltipPosition="right" [tooltipOptions]="{showDelay: 300}">
        <i [class]="healthSeverityIcon(item.severity) + ' ym-hc-icon'"></i>
        <span class="ym-hc-label">{{ item.label }}</span>
        <span class="ym-hc-count"
              [class.ym-hc-count-clickable]="item.count > 0"
              (click)="openDrillDown(item)">
          {{ item.count }}
        </span>
      </div>
    </div>

    <!-- ── Rate Health ──────────────────────────────────── -->
    <div class="ym-health-section">
      <div class="ym-health-section-title"><i class="pi pi-dollar"></i> Rates</div>
      <div class="ym-hc-row" *ngFor="let item of healthSummary.rateHealth"
           [class]="healthSeverityClass(item.severity)"
           [pTooltip]="item.description" tooltipPosition="right" [tooltipOptions]="{showDelay: 300}">
        <i [class]="healthSeverityIcon(item.severity) + ' ym-hc-icon'"></i>
        <span class="ym-hc-label">{{ item.label }}</span>
        <span class="ym-hc-count"
              [class.ym-hc-count-clickable]="item.count > 0"
              (click)="openDrillDown(item)">
          {{ item.count }}
        </span>
      </div>
    </div>

    <!-- ── Rate LOS Health ──────────────────────────────── -->
    <div class="ym-health-section">
      <div class="ym-health-section-title"><i class="pi pi-th-large"></i> Rate LOS</div>
      <div class="ym-hc-row" *ngFor="let item of healthSummary.rateLosHealth"
           [class]="healthSeverityClass(item.severity)"
           [pTooltip]="item.description" tooltipPosition="right" [tooltipOptions]="{showDelay: 300}">
        <i [class]="healthSeverityIcon(item.severity) + ' ym-hc-icon'"></i>
        <span class="ym-hc-label">{{ item.label }}</span>
        <span class="ym-hc-count"
              [class.ym-hc-count-clickable]="item.count > 0"
              (click)="openDrillDown(item)">
          {{ item.count }}
        </span>
      </div>
    </div>

  </div><!-- /ym-health-grid -->
  </ng-container>

  <ng-template #healthSpinner>
    <div class="ym-stats-loading">
      <i class="pi pi-spin pi-spinner"></i>
      <span>Running health checks…</span>
    </div>
  </ng-template>

</div><!-- /ym-health-panel -->

<!-- ── Drill-down Dialog ──────────────────────────────────── -->
<p-dialog [(visible)]="drillDownVisible"
  [header]="drillDownTitle"
  [modal]="true"
  [style]="{ width: '700px', maxHeight: '80vh' }"
  [draggable]="true"
  [resizable]="true">

  <div class="ym-dd-desc" *ngIf="drillDownDesc">{{ drillDownDesc }}</div>

  <p-table [value]="drillDownProperties"
    [paginator]="drillDownProperties.length > 15"
    [rows]="15"
    styleClass="p-datatable-sm p-datatable-striped"
    emptyMessage="No properties found.">
    <ng-template pTemplate="header">
      <tr>
        <th style="width:100px">ID</th>
        <th>Property Code</th>
        <th style="width:100px">Wiki ID</th>
        <th style="width:90px">Status</th>
        <th>Details</th>
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-p>
      <tr>
        <td>{{ p.propertyId }}</td>
        <td>
          <a [routerLink]="['/yield-max/property', p.propertyId]"
             target="_blank"
             class="ym-dd-prop-link">
            {{ p.propertyCode }}
          </a>
        </td>
        <td>
          <span [class.ym-dd-missing]="!p.extSystemId">
            {{ p.extSystemId || '—' }}
          </span>
        </td>
        <td>
          <p-tag [value]="p.listingStatusName"
            [severity]="p.listingStatusName === 'Active' ? 'success' : 'warning'">
          </p-tag>
        </td>
        <td class="ym-dd-additional">{{ p.additionalInfo || '' }}</td>
      </tr>
    </ng-template>
  </p-table>

  <ng-template pTemplate="footer">
    <button pButton label="Close" icon="pi pi-times"
      class="p-button p-button-sm p-button-outlined"
      (click)="drillDownVisible = false">
    </button>
  </ng-template>
</p-dialog>

<div class="ym-admin-container">

  <!-- ═══════════════════════════════════════════════════════════
       SECTION 1 — ALL PROPERTIES
  ════════════════════════════════════════════════════════════════ -->
  <div class="ym-section">
    <div class="ym-section-header">
      <i class="pi pi-globe"></i>
      <span>All Properties</span>
    </div>

    <div class="ym-action-row">
      <div class="ym-action-icon sync-icon"><i class="pi pi-refresh"></i></div>
      <div class="ym-action-info">
        <span class="ym-action-title">Sync All Properties</span>
        <span class="ym-action-desc">Pull latest property data from Wiki into YieldMax for all properties.</span>
      </div>
      <button pButton pRipple label="Sync All" icon="pi pi-refresh"
        class="p-button p-button-sm ym-btn-sync"
        [loading]="loadingSyncAllProperties"
        (click)="confirmSyncAllProperties()">
      </button>
    </div>

    <div class="ym-action-row">
      <div class="ym-action-icon sync-icon"><i class="pi pi-calendar"></i></div>
      <div class="ym-action-info">
        <span class="ym-action-title">Sync All Reservations</span>
        <span class="ym-action-desc">Pull latest reservation data from Wiki into YieldMax for all properties.</span>
      </div>
      <button pButton pRipple label="Sync All" icon="pi pi-refresh"
        class="p-button p-button-sm ym-btn-sync"
        [loading]="loadingSyncAllReservations"
        (click)="confirmSyncAllReservations()">
      </button>
    </div>

    <div class="ym-action-row">
      <div class="ym-action-icon generate-icon"><i class="pi pi-cog"></i></div>
      <div class="ym-action-info">
        <span class="ym-action-title">Generate All Rates &amp; LOS</span>
        <span class="ym-action-desc">Recalculate and push rates and LOS pricing for all active and suspended properties.</span>
      </div>
      <button pButton pRipple label="Generate All" icon="pi pi-cog"
        class="p-button p-button-sm ym-btn-generate"
        [loading]="loadingGenerateAllRates"
        (click)="confirmGenerateAllRates()">
      </button>
    </div>

    <div class="ym-action-row ym-action-row-warning">
      <div class="ym-action-icon warning-icon"><i class="pi pi-trash"></i></div>
      <div class="ym-action-info">
        <span class="ym-action-title">Surgical Remove LOS — All</span>
        <span class="ym-action-desc">Remove stale LOS records across all properties (older than 2 days, inactive, or below min LOS).</span>
      </div>
      <button pButton pRipple label="Remove Stale LOS" icon="pi pi-trash"
        class="p-button p-button-sm p-button-warning"
        [loading]="loadingSurgicalRemoveAll"
        (click)="confirmSurgicalRemoveAll()">
      </button>
    </div>
  </div>

  <!-- ═══════════════════════════════════════════════════════════
       SECTION 2 — BY PROPERTY
  ════════════════════════════════════════════════════════════════ -->
  <div class="ym-section">
    <div class="ym-section-header">
      <i class="pi pi-building"></i>
      <span>By Property</span>
      <p-multiSelect
        [options]="properties"
        [(ngModel)]="selectedProperties"
        optionLabel="label"
        placeholder="— Choose properties —"
        [filter]="true"
        filterBy="label"
        [showClear]="true"
        appendTo="body"
        styleClass="ym-property-dropdown"
        selectedItemsLabel="{0} properties selected"
        [maxSelectedLabels]="2"
        (onChange)="loadStats(); loadHealth()"
        (onClear)="loadStats(); loadHealth()">
      </p-multiSelect>
    </div>

    <div class="ym-action-row" [class.ym-disabled]="!hasSelection">
      <div class="ym-action-icon sync-icon"><i class="pi pi-refresh"></i></div>
      <div class="ym-action-info">
        <span class="ym-action-title">Sync Property</span>
        <span class="ym-action-desc">Pull the latest property data from Wiki into YieldMax for the selected property.</span>
      </div>
      <button pButton pRipple label="Sync" icon="pi pi-refresh"
        class="p-button p-button-sm ym-btn-sync"
        [loading]="loadingSyncProperty"
        [disabled]="!hasSelection"
        (click)="confirmSyncProperty()">
      </button>
    </div>

    <div class="ym-action-row" [class.ym-disabled]="!hasSelection">
      <div class="ym-action-icon sync-icon"><i class="pi pi-calendar"></i></div>
      <div class="ym-action-info">
        <span class="ym-action-title">Sync Reservations</span>
        <span class="ym-action-desc">Pull all reservations for the selected property from Wiki into YieldMax.</span>
      </div>
      <button pButton pRipple label="Sync" icon="pi pi-refresh"
        class="p-button p-button-sm ym-btn-sync"
        [loading]="loadingSyncReservations"
        [disabled]="!hasSelection"
        (click)="confirmSyncReservations()">
      </button>
    </div>

    <div class="ym-action-row" [class.ym-disabled]="!hasSelection">
      <div class="ym-action-icon generate-icon"><i class="pi pi-cog"></i></div>
      <div class="ym-action-info">
        <span class="ym-action-title">Generate Rates &amp; LOS</span>
        <span class="ym-action-desc">Sync reservations, recalculate rates and LOS pricing, then push to Wiki for the selected property.</span>
      </div>
      <button pButton pRipple label="Generate" icon="pi pi-cog"
        class="p-button p-button-sm ym-btn-generate"
        [loading]="loadingGenerateRates"
        [disabled]="!hasSelection"
        (click)="confirmGenerateRates()">
      </button>
    </div>

    <div class="ym-action-row ym-action-row-danger" [class.ym-disabled]="!hasSelection">
      <div class="ym-action-icon danger-icon"><i class="pi pi-exclamation-triangle"></i></div>
      <div class="ym-action-info">
        <span class="ym-action-title">Remove &amp; Regenerate All LOS</span>
        <span class="ym-action-desc"><strong>Destructive.</strong> Deletes all LOS records for the selected property, then immediately regenerates them.</span>
      </div>
      <button pButton pRipple label="Remove &amp; Regenerate" icon="pi pi-trash"
        class="p-button p-button-sm p-button-danger"
        [loading]="loadingRemoveRegenerateAll"
        [disabled]="!hasSelection"
        (click)="confirmRemoveRegenerateAll()">
      </button>
    </div>

    <div class="ym-action-row ym-action-row-warning" [class.ym-disabled]="!hasSelection">
      <div class="ym-action-icon warning-icon"><i class="pi pi-trash"></i></div>
      <div class="ym-action-info">
        <span class="ym-action-title">Surgical Remove LOS</span>
        <span class="ym-action-desc">Remove only stale LOS records for the selected property (safe cleanup — does not regenerate).</span>
      </div>
      <button pButton pRipple label="Surgical Remove" icon="pi pi-trash"
        class="p-button p-button-sm p-button-warning"
        [loading]="loadingSurgicalRemove"
        [disabled]="!hasSelection"
        (click)="confirmSurgicalRemoveByProperty()">
      </button>
    </div>

  </div>

  <!-- ═══════════════════════════════════════════════════════════
       SECTION 3 — SQS QUEUE STATUS
  ════════════════════════════════════════════════════════════════ -->
  <div class="ym-section ym-section-sqs">
    <div class="ym-section-header">
      <i class="pi pi-server"></i>
      <span>SQS Queue Status</span>
      <span class="ym-sqs-refresh-info" *ngIf="sqsLastRefresh">
        Last refreshed {{ sqsLastRefresh | date:'h:mm:ss a' }}
      </span>
      <button pButton pRipple icon="pi pi-refresh" label="Refresh"
        class="p-button p-button-sm p-button-outlined ym-sqs-refresh-btn"
        [loading]="loadingSqs"
        (click)="loadSqsStatus()">
      </button>
    </div>

    <div class="ym-sqs-grid">

      <div *ngFor="let q of sqsQueues"
           class="ym-sqs-card"
           [class.ym-sqs-backlog]="q.visibleMessages > 10"
           [class.ym-sqs-active]="q.visibleMessages > 0 || q.inFlightMessages > 0"
           [class.ym-sqs-error]="q.error">

        <!-- Card header -->
        <div class="ym-sqs-card-header">
          <span class="ym-sqs-label">{{ q.label }}</span>
          <span class="ym-sqs-badge" [class.ym-badge-nodlq]="!q.hasDlq" [class.ym-badge-dlq]="q.hasDlq">
            {{ q.hasDlq ? 'DLQ' : 'No DLQ' }}
          </span>
        </div>

        <!-- Direction + purpose -->
        <div class="ym-sqs-direction">{{ q.direction }}</div>
        <div class="ym-sqs-purpose"
             [pTooltip]="q.purpose" tooltipPosition="top" [tooltipOptions]="{showDelay: 300}">{{ q.purpose }}</div>

        <!-- Error state -->
        <div *ngIf="q.error" class="ym-sqs-error-msg">
          <i class="pi pi-exclamation-circle"></i> {{ q.errorMessage || 'Failed to load queue attributes' }}
        </div>

        <!-- Stats row -->
        <div *ngIf="!q.error" class="ym-sqs-stats">
          <div class="ym-sqs-stat"
               pTooltip="Big number = messages waiting in the queue RIGHT NOW (not yet processed). Below it: total messages added in the last 24 hours, and the average added per hour. Example: 10 waiting now, 100 added in 24h, ~4/hr. A steadily rising 'waiting now' number means the consumer is falling behind."
               tooltipPosition="top" [tooltipOptions]="{showDelay: 300}">
            <span class="ym-sqs-stat-value" [class.ym-stat-warning]="q.visibleMessages > 10">{{ q.visibleMessages }}</span>
            <span class="ym-sqs-stat-sub" *ngIf="q.queuedLast24h != null">{{ q.queuedLast24h | number }} in 24h</span>
            <span class="ym-sqs-stat-sub" *ngIf="q.avgPerHour != null">avg {{ q.avgPerHour | number:'1.0-1' }}/hr</span>
            <span class="ym-sqs-stat-label">Queued</span>
          </div>
          <div class="ym-sqs-stat">
            <span class="ym-sqs-stat-value" [class.ym-stat-active]="q.inFlightMessages > 0">{{ q.inFlightMessages }}</span>
            <span class="ym-sqs-stat-label">In-Flight</span>
          </div>
          <div class="ym-sqs-stat">
            <span class="ym-sqs-stat-value">{{ q.delayedMessages }}</span>
            <span class="ym-sqs-stat-label">Delayed</span>
          </div>
          <div class="ym-sqs-stat">
            <span class="ym-sqs-stat-value ym-stat-dim">{{ q.visibilityTimeoutSeconds }}s</span>
            <span class="ym-sqs-stat-label">Visibility</span>
          </div>
          <div class="ym-sqs-stat">
            <span class="ym-sqs-stat-value ym-stat-dim">{{ formatAvgTime(q.avgProcessingMs) }}</span>
            <span class="ym-sqs-stat-label">Avg/Msg</span>
          </div>
        </div>

      </div>

      <!-- Loading skeleton -->
      <div *ngIf="loadingSqs && sqsQueues.length === 0" class="ym-sqs-loading">
        <i class="pi pi-spin pi-spinner"></i> Loading queue stats&hellip;
      </div>

    </div>
  </div>

  <!-- ═══════════════════════════════════════════════════════════
       SECTION 4 — QUICK LINKS
  ════════════════════════════════════════════════════════════════ -->
  <div class="ym-section ym-section-links">
    <div class="ym-section-header">
      <i class="pi pi-link"></i>
      <span>Quick Links</span>
    </div>
    <div class="ym-links-grid">

      <a routerLink="/yield-max" target="_blank" class="ym-link-card">
        <i class="pi pi-chart-bar ym-link-icon"></i>
        <span class="ym-link-title">YieldMax</span>
        <span class="ym-link-desc">Rates, tape chart, property config</span>
      </a>

      <a routerLink="/ops-dashboard" target="_blank" class="ym-link-card">
        <i class="pi pi-desktop ym-link-icon"></i>
        <span class="ym-link-title">Ops Dashboard</span>
        <span class="ym-link-desc">Sync status, error lists, processing times</span>
      </a>

      <a routerLink="/logmans-yieldmax" target="_blank" class="ym-link-card">
        <i class="pi pi-list ym-link-icon"></i>
        <span class="ym-link-title">YieldMax Logs</span>
        <span class="ym-link-desc">Property-level logman entries</span>
      </a>

      <a href="https://yieldmax.io" target="_blank" rel="noopener" class="ym-link-card ym-link-external">
        <i class="pi pi-external-link ym-link-icon"></i>
        <span class="ym-link-title">YieldMax API</span>
        <span class="ym-link-desc">Raw API — eApps WF31 box</span>
      </a>

    </div>
  </div>

  <!-- ═══════════════════════════════════════════════════════════
       SECTION 5 — RATE REGEN (BOX-TARGETED)  [Task #86]
  ════════════════════════════════════════════════════════════════ -->
  <div class="ym-section ym-section-regen" style="flex-basis: 100%;">
    <div class="ym-section-header">
      <i class="pi pi-server"></i>
      <span>Rate Regen — Box-Targeted</span>
      <span class="ym-section-subtitle">POST directly to ATL-RPT or NYC-RPT; HTTP 202 + polling every 5s</span>
    </div>

    <div class="ym-regen-boxes">

      <!-- ATL-RPT -->
      <div class="ym-regen-box">
        <div class="ym-regen-box-header">
          <i class="pi pi-database"></i>
          <span class="ym-regen-box-name">ATL-RPT</span>
          <span class="ym-regen-box-url">atl-rpt.kbmhawaii.com</span>
        </div>

        <div class="ym-action-row">
          <div class="ym-action-icon generate-icon"><i class="pi pi-cog"></i></div>
          <div class="ym-action-info">
            <span class="ym-action-title">Generate All (scope=ALL)</span>
            <span class="ym-action-desc">Regen rates + LOS for every active STR on this box.</span>
          </div>
          <button pButton pRipple label="Regen All" icon="pi pi-cog"
            class="p-button p-button-sm ym-btn-generate"
            [loading]="atlAllLoading"
            (click)="triggerAtlAll()">
          </button>
        </div>

        <div class="ym-action-row">
          <div class="ym-action-icon generate-icon"><i class="pi pi-sliders-h"></i></div>
          <div class="ym-action-info ym-range-inputs">
            <span class="ym-action-title">Range (scope=RANGE)</span>
            <div class="ym-range-row">
              <p-inputNumber [(ngModel)]="atlRangeStart" placeholder="STR ID start" [useGrouping]="false" styleClass="ym-range-input"></p-inputNumber>
              <span class="ym-range-dash">–</span>
              <p-inputNumber [(ngModel)]="atlRangeEnd"   placeholder="STR ID end"   [useGrouping]="false" styleClass="ym-range-input"></p-inputNumber>
            </div>
          </div>
          <button pButton pRipple label="Regen Range" icon="pi pi-sliders-h"
            class="p-button p-button-sm ym-btn-generate"
            [loading]="atlRangeLoading"
            [disabled]="!atlRangeStart || !atlRangeEnd"
            (click)="triggerAtlRange()">
          </button>
        </div>

        <!-- ATL active job status -->
        <div *ngIf="atlActiveJob" class="ym-job-status-card">
          <div class="ym-job-status-header">
            <span class="ym-job-id">Job #{{ atlActiveJob.id }}</span>
            <p-tag *ngIf="atlActiveJob.log"
              [value]="jobStatusLabel(atlActiveJob.log.jobStatus)"
              [severity]="jobStatusSeverity(atlActiveJob.log.jobStatus)">
            </p-tag>
            <p-tag *ngIf="!atlActiveJob.log" value="Queued" severity="info"></p-tag>
          </div>
          <div *ngIf="atlActiveJob.log" class="ym-job-progress">
            <p-progressBar
              [value]="jobProgressPercent(atlActiveJob.log)"
              styleClass="ym-prog-bar">
            </p-progressBar>
            <span class="ym-job-counts">{{ atlActiveJob.log.processedItems }} / {{ atlActiveJob.log.totalItems }} STRs</span>
          </div>
        </div>
      </div>

      <!-- NYC-RPT -->
      <div class="ym-regen-box">
        <div class="ym-regen-box-header">
          <i class="pi pi-database"></i>
          <span class="ym-regen-box-name">NYC-RPT</span>
          <span class="ym-regen-box-url">nyc-rpt.kbmhawaii.com</span>
        </div>

        <div class="ym-action-row">
          <div class="ym-action-icon generate-icon"><i class="pi pi-cog"></i></div>
          <div class="ym-action-info">
            <span class="ym-action-title">Generate All (scope=ALL)</span>
            <span class="ym-action-desc">Regen rates + LOS for every active STR on this box.</span>
          </div>
          <button pButton pRipple label="Regen All" icon="pi pi-cog"
            class="p-button p-button-sm ym-btn-generate"
            [loading]="nycAllLoading"
            (click)="triggerNycAll()">
          </button>
        </div>

        <div class="ym-action-row">
          <div class="ym-action-icon generate-icon"><i class="pi pi-sliders-h"></i></div>
          <div class="ym-action-info ym-range-inputs">
            <span class="ym-action-title">Range (scope=RANGE)</span>
            <div class="ym-range-row">
              <p-inputNumber [(ngModel)]="nycRangeStart" placeholder="STR ID start" [useGrouping]="false" styleClass="ym-range-input"></p-inputNumber>
              <span class="ym-range-dash">–</span>
              <p-inputNumber [(ngModel)]="nycRangeEnd"   placeholder="STR ID end"   [useGrouping]="false" styleClass="ym-range-input"></p-inputNumber>
            </div>
          </div>
          <button pButton pRipple label="Regen Range" icon="pi pi-sliders-h"
            class="p-button p-button-sm ym-btn-generate"
            [loading]="nycRangeLoading"
            [disabled]="!nycRangeStart || !nycRangeEnd"
            (click)="triggerNycRange()">
          </button>
        </div>

        <!-- NYC active job status -->
        <div *ngIf="nycActiveJob" class="ym-job-status-card">
          <div class="ym-job-status-header">
            <span class="ym-job-id">Job #{{ nycActiveJob.id }}</span>
            <p-tag *ngIf="nycActiveJob.log"
              [value]="jobStatusLabel(nycActiveJob.log.jobStatus)"
              [severity]="jobStatusSeverity(nycActiveJob.log.jobStatus)">
            </p-tag>
            <p-tag *ngIf="!nycActiveJob.log" value="Queued" severity="info"></p-tag>
          </div>
          <div *ngIf="nycActiveJob.log" class="ym-job-progress">
            <p-progressBar
              [value]="jobProgressPercent(nycActiveJob.log)"
              styleClass="ym-prog-bar">
            </p-progressBar>
            <span class="ym-job-counts">{{ nycActiveJob.log.processedItems }} / {{ nycActiveJob.log.totalItems }} STRs</span>
          </div>
        </div>
      </div>

    </div><!-- /ym-regen-boxes -->
  </div>

  <!-- ═══════════════════════════════════════════════════════════
       SECTION 6 — HEALTH & JOBS  [Task #95]
  ════════════════════════════════════════════════════════════════ -->
  <div class="ym-section" style="flex-basis: 100%;">
    <div class="ym-section-header">
      <i class="pi pi-list"></i>
      <span>Health &amp; Jobs — Rate Regen</span>
      <span class="ym-sqs-refresh-info" *ngIf="jobHistory.length > 0">
        {{ jobHistory.length }} job(s) this session
      </span>
    </div>

    <p-table
      [value]="jobHistory"
      [paginator]="jobHistory.length > 10"
      [rows]="10"
      styleClass="p-datatable-sm"
      emptyMessage="No regen jobs submitted this session.">

      <ng-template pTemplate="header">
        <tr>
          <th style="width:80px">Job ID</th>
          <th style="width:70px">Box</th>
          <th style="width:130px">Scope</th>
          <th style="width:110px">Status</th>
          <th style="width:90px">Processed</th>
          <th style="width:70px">Total</th>
          <th>Started</th>
          <th>Ended</th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-row>
        <tr>
          <td><span class="ym-job-id-cell">{{ row.id }}</span></td>
          <td>
            <p-tag [value]="row.box"
              [severity]="row.box === 'ATL' ? 'primary' : 'secondary'">
            </p-tag>
          </td>
          <td><span class="ym-scope-label">{{ row.scope }}</span></td>
          <td>
            <p-tag *ngIf="row.log"
              [value]="jobStatusLabel(row.log.jobStatus)"
              [severity]="jobStatusSeverity(row.log.jobStatus)">
            </p-tag>
            <p-tag *ngIf="!row.log" value="Queued" severity="info"></p-tag>
          </td>
          <td class="ym-num-cell">{{ row.log?.processedItems ?? '—' }}</td>
          <td class="ym-num-cell">{{ row.log?.totalItems ?? '—' }}</td>
          <td class="ym-date-cell">{{ (row.log?.startTime | date:'MM/dd h:mm a') || '—' }}</td>
          <td class="ym-date-cell">{{ (row.log?.endTime   | date:'MM/dd h:mm a') || '—' }}</td>
        </tr>
      </ng-template>
    </p-table>
  </div>

</div><!-- /.ym-admin-container -->

<!-- ═══════════════════════════════════════════════════════════
     PER-PROPERTY SYNC TABLE (last 24 hours)
════════════════════════════════════════════════════════════════ -->
<div class="ym-propsync-panel">

  <p-table #propSyncTable
    [value]="propertyRows"
    [paginator]="propertyRows.length > 25"
    [rows]="25"
    [rowsPerPageOptions]="[25, 50, 100]"
    [globalFilterFields]="['propertyCode','extSystemId','propertyId']"
    sortMode="single"
    styleClass="p-datatable-sm p-datatable-striped p-datatable-gridlines"
    [scrollable]="true" scrollHeight="520px"
    emptyMessage="No active properties found.">

    <!-- Caption: title + count + search -->
    <ng-template pTemplate="caption">
      <div class="ym-propsync-caption">
        <div class="ym-propsync-title">
          <i class="pi pi-table"></i>
          <span>Sync Activity by Property</span>
          <span class="ym-stats-subtitle">
            Last 24 hours — how many times each property synced (manual + scheduled). {{ propertyRows.length }} active properties.
          </span>
        </div>
        <span class="p-input-icon-left ym-propsync-search">
          <i class="pi pi-search"></i>
          <input pInputText type="text" [(ngModel)]="propertyRowsFilter"
            (input)="propSyncTable.filterGlobal(propertyRowsFilter, 'contains')"
            placeholder="Search code / Wiki ID…" />
        </span>
      </div>
    </ng-template>

    <ng-template pTemplate="header">
      <tr>
        <th pSortableColumn="propertyCode" style="min-width:130px"
            pTooltip="The KBM property code. Click a code to open that property in YieldMax."
            tooltipPosition="top" [tooltipOptions]="{showDelay: 400}">
          Property <p-sortIcon field="propertyCode"></p-sortIcon>
        </th>
        <th pSortableColumn="extSystemId" style="min-width:90px"
            pTooltip="The Wiki / STR id used to push data back to the booking site. Blank = data cannot reach Wiki."
            tooltipPosition="top" [tooltipOptions]="{showDelay: 400}">
          Wiki ID <p-sortIcon field="extSystemId"></p-sortIcon>
        </th>
        <th pSortableColumn="propertySync" class="ym-ps-num"
            pTooltip="Times the PROPERTY profile (beds, status, etc.) was synced for this property in the last 24h. Manual 'Sync Property' clicks also count here."
            tooltipPosition="top" [tooltipOptions]="{showDelay: 400}">
          Property <p-sortIcon field="propertySync"></p-sortIcon>
        </th>
        <th pSortableColumn="reservationSync" class="ym-ps-num"
            pTooltip="Times RESERVATIONS were synced for this property in the last 24h. Example: 3 means 3 reservation-sync runs since yesterday."
            tooltipPosition="top" [tooltipOptions]="{showDelay: 400}">
          Reservation <p-sortIcon field="reservationSync"></p-sortIcon>
        </th>
        <th pSortableColumn="rateSync" class="ym-ps-num"
            pTooltip="Times nightly RATES were (re)generated/synced for this property in the last 24h."
            tooltipPosition="top" [tooltipOptions]="{showDelay: 400}">
          Rate <p-sortIcon field="rateSync"></p-sortIcon>
        </th>
        <th pSortableColumn="rateLosSync" class="ym-ps-num"
            pTooltip="Times the length-of-stay (LOS) pricing grid was (re)generated/synced for this property in the last 24h."
            tooltipPosition="top" [tooltipOptions]="{showDelay: 400}">
          Rate LOS <p-sortIcon field="rateLosSync"></p-sortIcon>
        </th>
        <th class="ym-ps-num ym-ps-errcol"
            pTooltip="Total FAILED sync runs for this property in the last 24h (any type). Sort by this to find the worst-behaving properties first."
            tooltipPosition="top" [tooltipOptions]="{showDelay: 400}">
          Errors
        </th>
        <th pSortableColumn="lastSyncDate" style="min-width:130px"
            pTooltip="When this property last had ANY sync activity. 'Never' / blank means nothing synced in the last 24h."
            tooltipPosition="top" [tooltipOptions]="{showDelay: 400}">
          Last Sync <p-sortIcon field="lastSyncDate"></p-sortIcon>
        </th>
      </tr>
    </ng-template>

    <ng-template pTemplate="body" let-row>
      <tr [class.ym-ps-row-error]="propertyTotalErrors(row) > 0"
          [class.ym-ps-row-idle]="propertyTotalSyncs(row) === 0 && propertyTotalErrors(row) === 0">
        <td>
          <a [routerLink]="['/yield-max/property', row.propertyId]" target="_blank" class="ym-dd-prop-link">
            {{ row.propertyCode || row.propertyId }}
          </a>
        </td>
        <td>
          <span [class.ym-dd-missing]="!row.extSystemId">{{ row.extSystemId || '—' }}</span>
        </td>

        <!-- Each success count links (new tab) to the logman list filtered by this property + type + SUCCESS + today -->
        <td class="ym-ps-num" [class.ym-ps-zero]="!row.propertySync">
          <a *ngIf="row.propertySync > 0" target="_blank" class="ym-ps-link"
             [routerLink]="['/logmans-yieldmax']"
             [queryParams]="{ referenceId: 'SYNC_EXTERNAL_PROPERTY', severity: 'SUCCESS', propertyId: row.propertyId, day: todayStr }">{{ row.propertySync | number }}</a>
          <span *ngIf="!row.propertySync">0</span>
        </td>
        <td class="ym-ps-num" [class.ym-ps-zero]="!row.reservationSync">
          <a *ngIf="row.reservationSync > 0" target="_blank" class="ym-ps-link"
             [routerLink]="['/logmans-yieldmax']"
             [queryParams]="{ referenceId: 'SYNC_EXTERNAL_RESERVATION', severity: 'SUCCESS', propertyId: row.propertyId, day: todayStr }">{{ row.reservationSync | number }}</a>
          <span *ngIf="!row.reservationSync">0</span>
        </td>
        <td class="ym-ps-num" [class.ym-ps-zero]="!row.rateSync">
          <a *ngIf="row.rateSync > 0" target="_blank" class="ym-ps-link"
             [routerLink]="['/logmans-yieldmax']"
             [queryParams]="{ referenceId: 'SYNC_RATES', severity: 'SUCCESS', propertyId: row.propertyId, day: todayStr }">{{ row.rateSync | number }}</a>
          <span *ngIf="!row.rateSync">0</span>
        </td>
        <td class="ym-ps-num" [class.ym-ps-zero]="!row.rateLosSync">
          <a *ngIf="row.rateLosSync > 0" target="_blank" class="ym-ps-link"
             [routerLink]="['/logmans-yieldmax']"
             [queryParams]="{ referenceId: 'SYNC_RATES_LOS', severity: 'SUCCESS', propertyId: row.propertyId, day: todayStr }">{{ row.rateLosSync | number }}</a>
          <span *ngIf="!row.rateLosSync">0</span>
        </td>

        <!-- Errors total links (new tab) to all ERROR logman rows for this property today -->
        <td class="ym-ps-num ym-ps-errcol">
          <a *ngIf="propertyTotalErrors(row) > 0" target="_blank" class="ym-ps-link ym-ps-link-err"
             [routerLink]="['/logmans-yieldmax']"
             [queryParams]="{ severity: 'ERROR', propertyId: row.propertyId, day: todayStr }">{{ propertyTotalErrors(row) | number }}</a>
          <span *ngIf="propertyTotalErrors(row) === 0" class="ym-ps-zero">0</span>
        </td>

        <td class="ym-date-cell">{{ row.lastSyncDate || '—' }}</td>
      </tr>
    </ng-template>
  </p-table>

</div><!-- /.ym-propsync-panel -->

</div><!-- /.ym-admin-wrapper -->
